import React, { useState, useEffect, useCallback } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FaCheck, FaPen } from 'react-icons/fa6';
import { MdOutlineAdd } from 'react-icons/md';
import toast from 'react-hot-toast';

import Loader from './custom/Loader';
import { integrationData } from '../utils/constants';
import { authenticateApp } from '../utils/auth'; // Your auth.js
import { getAuthenticatedApps, unauthApp } from '../services/api';
import ResourceModal from './modals/ResourceModal';
import { useEmails } from '../context/EmailsProvider';
import { DataState } from '../context/DataProvider';
import { useTeams } from '../context/TeamsProvider';
import McpConnectionManager from './McpConnections/McpConnectionManager';
import { useMCP } from '../context/McpProvider';
import WorkEmailCapture from './premiumFeatureComponent/WorkEmailCapture';
import JiraResourceModal from './modals/JiraResourceModal';
import ConfluenceResourceModal from './modals/ConfluenceResourceModal';
import TeamsConfigurationModal from './modals/TeamsConfigurationModal';
import GohighlevelResourceModal from './modals/GohighlevelResourceModal';
import ShiprocketAuthModal from './modals/ShiprocketAuthModal';
import RazorpayAuthModal from './modals/RazorpayAuthModal';

const Integration = ({
  onClose,
  user_google_id,
  bot_id,
  onlyEmailApps = false,
  isTeamsInitialized,
}) => {
  const [loading, setLoading] = useState(false);
  const [integratedApps, setIntegratedApps] = useState([]);
  const [notIntegratedApps, setNotIntegratedApps] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAppName, setModalAppName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPremium, setShowPremium] = useState(false);
  const { refreshConnections } = useMCP();
  const [activeTab, setActiveTab] = useState('Apps');

  // NEW: State for controlling the MCP Connection Manager modal
  const isMcpModalOpen = false;

  const { userInfo } = DataState();
  const { isTeamsInitialized: isTeamsInitFromDataState } = useTeams();
  const { setGmailAuthStatus, setOutlookAuthStatus } = useEmails();

  // Use the prop if passed, otherwise fallback to context or assume false
  const inTeams =
    typeof isTeamsInitialized === 'boolean'
      ? isTeamsInitialized
      : isTeamsInitFromDataState || false;

  //preparing apps_data based on onlyEmailApps flag
  let apps_data = [...integrationData];

  if (onlyEmailApps) {
    apps_data = apps_data.filter((app) =>
      ['gmail', 'microsoft_outlook'].includes(app.value),
    );
  } else {
    apps_data = apps_data.map((app, unique_index) => {
      app.unique_index = unique_index;
      return app;
    });
  }

  const fetchIntegratedApps = async (selectedAppForIntegration = null) => {
    setLoading(true);
    try {
      const result = await getAuthenticatedApps(user_google_id, bot_id);
      const integrated = result.apps;

      // here I want to insert the redirect_url from the result into each app in apps client config state
      const updatedAppsClientConfig = { ...(result.apps_client_config || {}) };
      integrated.forEach((app) => {
        if (result.apps_client_config[app]) {
          updatedAppsClientConfig[app] = {
            client_id: result.apps_client_config[app].client_id || '',
            client_secret: result.apps_client_config[app].client_secret || '',
            use_user_client:
              result.apps_client_config[app].use_user_client || false,
            redirect_uri: result.redirect_uri || '',
          };
        }
      });

      const integratedAppsFiltered = apps_data.filter((app) =>
        integrated.includes(app.value),
      );
      const notIntegratedAppsFiltered = apps_data.filter(
        (app) => !integrated.includes(app.value),
      );
      setIntegratedApps(integratedAppsFiltered);
      setNotIntegratedApps(notIntegratedAppsFiltered);
    } catch (error) {
      toast.error('Failed to fetch integrated apps');
    } finally {
      refreshConnections();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegratedApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_google_id, bot_id]);

  // Global keydown handler for building the search term
  const handleKeyDown = useCallback(
    (e) => {
      // NEW: Disable search listener if any modal or premium view is open
      if (isModalOpen || isMcpModalOpen || showPremium) return;

      // Ignore if any modifier keys are pressed
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      // if (e.key === "Backspace") {
      //   setSearchTerm((prev) => prev.slice(0, -1));
      if (e.key === 'Backspace' || e.key === 'Delete') {
        setSearchTerm('');
      } else if (e.key && e.key.length === 1) {
        // Append all printable characters including space
        setSearchTerm((prev) => prev + e.key);
      }
    },
    [isModalOpen, isMcpModalOpen, showPremium], // Add these dependencies so the listener updates when state changes
  );
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Filter apps based on a fuzzy regex match from the search term.
  const filterApps = (apps) => {
    if (!searchTerm) return apps;
    try {
      // Create a fuzzy regex pattern, e.g. "olook" => "o.*l.*o.*o.*k"
      const pattern = searchTerm.split('').join('.*');
      const regex = new RegExp(pattern, 'i');
      return apps.filter((app) => regex.test(app.label));
    } catch (error) {
      error('Regex error:', error);
      return apps;
    }
  };

  // -------- AUTH --------
  const handleIntegrateButtonClick = async (app) => {
    const googleApps = [
      'gmail',
      'google_drive',
      'google_calendar',
      'google_docs',
      'google_sheets',
      'google_forms',
      'google_tasks',
      'google_keep',
    ];

    if (
      googleApps.includes(app.value) &&
      (!userInfo || !userInfo.enterprise_access)
    ) {
      setShowPremium(true);
      return;
    }

    if (app.value === 'shiprocket') {
      setModalAppName('shiprocket');
      setIsModalOpen(true);
      return;
    }

    if (app.value === 'razorpay') {
      setModalAppName('razorpay');
      setIsModalOpen(true);
      return;
    }

    setLoading(true);

    await authenticateApp(
      app.value,
      user_google_id,
      async () => {
        await fetchIntegratedApps();
        setLoading(false);
      },
      (appName) => {
        if (onlyEmailApps && appName === 'gmail') {
          setGmailAuthStatus((p) => ({ status: true, state: p.state + 1 }));
        }
        if (onlyEmailApps && appName === 'microsoft_outlook') {
          setOutlookAuthStatus((p) => ({ status: true, state: p.state + 1 }));
        }
      },
      inTeams,
    );
  };

  const handleUnAuthorisationButtonClick = async (selectedAppForUnauth) => {
    if (selectedAppForUnauth) {
      setLoading(true);
      await unauthApp(selectedAppForUnauth.value, bot_id, user_google_id);
      if (onlyEmailApps) {
        if (selectedAppForUnauth.value === 'gmail') {
          setGmailAuthStatus((prev) => ({
            status: false,
            state: prev.state + 1,
          }));
        } else if (selectedAppForUnauth.value === 'microsoft_outlook') {
          setOutlookAuthStatus((prev) => ({
            status: false,
            state: prev.state + 1,
          }));
        }
      }
      fetchIntegratedApps();
    }
  };

  const openModal = (appName) => {
    setModalAppName(appName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalAppName('');
  };

  const filteredIntegratedApps = filterApps(integratedApps);
  const filteredNotIntegratedApps = filterApps(notIntegratedApps);

  const TABS = ['Apps', 'Custom MCP'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <button
        className="absolute inset-0 block bg-black opacity-50"
        onClick={onClose}
      />

      {showPremium ? (
        <div className="z-10 w-[90%] max-w-6xl">
          <WorkEmailCapture setShowPremium={setShowPremium} />
        </div>
      ) : (
        <div
          className={`bg-1 text-p-text relative z-10 flex h-[90%]      w-[90%] max-w-3xl flex-col rounded-2xl border border-zinc-700
         ${onlyEmailApps ? 'h-fit' : 'max-h-[70%] overflow-hidden'}
         `}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-zinc-700 px-6 py-5">
            <h2 className="text-xl font-semibold text-zinc-200">
              {onlyEmailApps ? 'Your MailBoxes' : 'Your Integrations'}
            </h2>
            <button onClick={onClose}>
              <IoMdClose className="text-zinc-400" size={20} />
            </button>
          </div>

          {/* Tabs + Search */}
          <div className="flex items-center justify-between border-b border-zinc-700 px-6 pt-4">
            <div className="flex gap-8">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-sm ${
                    activeTab === tab
                      ? 'border-b-2 border-white text-white'
                      : 'text-zinc-400'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[65vh] overflow-y-auto px-6 py-6">
            {loading ? (
              <div
                className={`text-p-text flex items-center justify-center pl-4 pr-4  ${onlyEmailApps ? '' : 'h-64'} `}
              >
                <span className="h-12">
                  <Loader />
                </span>
              </div>
            ) : (
              <>
                {/* ===================== APPS TAB ===================== */}
                {activeTab === 'Apps' && (
                  <div
                    className={`flex flex-wrap gap-x-4 gap-y-4 max-sm:flex-col max-sm:items-center ${onlyEmailApps ? 'items-center justify-center pt-0' : ''}`}
                  >
                    {filteredIntegratedApps.map((item) => (
                      <div
                        key={item.unique_index}
                        className="group flex w-fit items-center gap-2 rounded-full border border-zinc-400 p-1"
                      >
                        <div className="rounded-full bg-zinc-200 p-1">
                          <img
                            alt={item.label}
                            src={item.image}
                            className="h-5 w-5"
                          />
                        </div>
                        <div className="text-p-text text-md ml-1 text-center font-semibold">
                          {item.label}
                        </div>
                        {[
                          'jira',
                          'confluence',
                          'azure_devops',
                          'microsoft_outlook',
                          'github',
                          'gmail',
                          'microsoft_teams',
                          'gohighlevel',
                        ].includes(item.value) && (
                          <button
                            onClick={() => openModal(item.value)}
                            className="ml-3 hidden rounded-full bg-white p-2 text-blue-700 transition-all duration-300 hover:text-blue-800 group-hover:block"
                          >
                            <FaPen size={10} />
                          </button>
                        )}
                        <button
                          onClick={() => handleUnAuthorisationButtonClick(item)}
                          className="rounded-full bg-green-700 p-2 text-xs transition-all duration-300 hover:bg-green-800"
                        >
                          <FaCheck size={10} />
                        </button>
                      </div>
                    ))}
                    {filteredNotIntegratedApps.map((item) => (
                      <div
                        key={`Not-integrated-app-${item.unique_index}`}
                        className="flex w-fit items-center gap-2 rounded-full border border-zinc-400 p-1"
                      >
                        <div className="rounded-full bg-zinc-200 p-1">
                          <img
                            alt={item.label}
                            src={item.image}
                            className="h-5 w-5"
                          />
                        </div>
                        <div className="text-p-text text-md ml-1 text-center font-semibold">
                          {item.label}
                        </div>
                        <button
                          onClick={() => handleIntegrateButtonClick(item)}
                          className="ml-4 rounded-full bg-zinc-100 p-2 text-xs font-bold text-zinc-700 transition-all duration-300 hover:bg-zinc-300"
                        >
                          <MdOutlineAdd size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* ===================== CUSTOM MCP TAB ===================== */}
                {activeTab === 'Custom MCP' && <McpConnectionManager />}
              </>
            )}
          </div>

          {isModalOpen && (
            <>
              {modalAppName === 'jira' ? (
                <JiraResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                />
              ) : modalAppName === 'confluence' ? (
                <ConfluenceResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                />
              ) : modalAppName === 'microsoft_teams' ? (
                <TeamsConfigurationModal onClose={closeModal} />
              ) : modalAppName === 'gohighlevel' ? (
                <GohighlevelResourceModal onClose={closeModal} />
              ) : modalAppName === 'shiprocket' ? (
                <ShiprocketAuthModal
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onSuccess={() => {
                    closeModal();
                    fetchIntegratedApps();
                  }}
                />
              ) : modalAppName === 'razorpay' ? (
                <RazorpayAuthModal
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onSuccess={() => {
                    closeModal();
                    fetchIntegratedApps();
                  }}
                />
              ) : (
                <ResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onlyEmailApps={onlyEmailApps}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Integration;
